import type { Socket } from 'socket.io-client';

type Handler = (payload: any) => void;

/**
 * One authenticated socket per tab (opened by NotificationsProvider) carries
 * every realtime event: notifications, support messages, session sign-outs.
 * Features subscribe here instead of opening their own connection; handlers
 * registered before the socket connects still receive its events.
 */
const handlers = new Map<string, Set<Handler>>();
let socket: Socket | null = null;

const dispatch = (event: string, payload: any) => {
  handlers.get(event)?.forEach((h) => {
    try {
      h(payload);
    } catch (e) {
      // One broken listener must not stop the others.
      console.error(`[realtime] ${event} handler failed`, e);
    }
  });
};

export function attachRealtimeSocket(next: Socket): void {
  socket = next;
  next.onAny(dispatch);
}

export function detachRealtimeSocket(current: Socket): void {
  current.offAny(dispatch);
  if (socket === current) socket = null;
}

export function onRealtime(event: string, handler: Handler): () => void {
  if (!handlers.has(event)) handlers.set(event, new Set());
  handlers.get(event)!.add(handler);
  return () => {
    handlers.get(event)?.delete(handler);
  };
}

export function isRealtimeConnected(): boolean {
  return !!socket?.connected;
}

/** Test hook: deliver an event as if the socket had received it. */
export function emitRealtimeForTest(event: string, payload: any): void {
  dispatch(event, payload);
}
